import React, { useEffect, useState, useCallback } from 'react'
import { AppBar, Box, Dialog, DialogContent, Grid, Toolbar, IconButton, Tooltip, CircularProgress } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Logout from '@mui/icons-material/Logout'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../reducers'
import { useStyles } from './CaseDiagnosisStyles'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { AccountCircle } from '@mui/icons-material'
import { useMsal } from '@azure/msal-react'
import { Logo } from '../Common/Logo'
import { setSaveState, userLogout } from '../../actions'
import { UserProfileView } from '../UserManagement/UserProfileView'
import { DiagnosisNavbar } from './DiagnosisNavbar'
import Summary from './DiagnosisSummary/Summary'
import Viewer from './SlideViewer/Viewer'
import Report from './ReportGenerator/Report'
import { addListData, getSlideDiagnosisOptions, getReportHistory } from '../../actions'

export default function LaunchPremeris({
  caseView,
  handlePremerisClose,
  handleTRFComplete,
  isReadOnlyMode,
  urlLink,
  isAmendFlow,
}) {
  const { role, name, user } = useSelector((state: RootState) => state.userLogin)
  const trfData = useSelector((state: RootState) => state.addTrfInfo)
  const { caseNumber, procedureCodeId, workflowId } = useSelector((state: RootState) => state.getCaseData)
  const sampleInfoTree = useSelector((state: RootState) => state.addSampleLocationData)
  const classes = useStyles()
  const dispatch = useDispatch()
  const { instance, accounts } = useMsal()
  const [summaryOpen, setSummaryOpen] = useState(true)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [isShow, setShow] = useState(false)
  const [trfBarcode, setTrfBarcode] = useState('')
  const [slide, setSlide] = useState('')
  const [stain, setStain] = useState('')
  const [classifier, setClassifier] = useState('')
  const [location, setLocation] = useState('')
  const [pseudoProcedureCode, setPseudoProcedureCode] = useState('')
  const [stainsDropdown, setStainsDropdown] = useState([] as any)
  const [profileOpen, setProfileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const handleSettingsOpen = Boolean(anchorEl)
  const barcodeData = useSelector((state: RootState) => state.addBarcodeData)
  const [readOnlyMode, setIsReadOnlyMode] = useState(isReadOnlyMode)
  const [isDiagnosed, setDiagnosed] = useState(false)
  const [isDontReport, setDontReport] = useState(false)
  const [barcodeError, setBarcodeError] = useState<{ message: string } | null>(null)

  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  useEffect(() => {
    if (location) {
      const currentLocationData = sampleInfoTree['payload'].filter((item) => item.location === location)
      if (currentLocationData.length) {
        setPseudoProcedureCode(currentLocationData[0].procedureCode)
      }
    }
  }, [location])

  useEffect(() => {
    if (caseNumber !== '') {
      setTrfBarcode(caseNumber + '000')
      dispatch(addListData([], true))
      dispatch(setSaveState({ isSaveDisabled: true }))
    }
  }, [caseNumber])

  useEffect(() => {
    if (procedureCodeId) {
      dispatch(getSlideDiagnosisOptions({ procedureCode: procedureCodeId }))
    }
  }, [])

  useEffect(() => {
    if (!barcodeData || !barcodeData.data || !barcodeData.data.versions || barcodeData.data.versions.length === 0) {
      if (barcodeData?.error) {
        console.log('Setting barcodeError with API error:', barcodeData.error)
        setBarcodeError({
          message: barcodeData.error,
        })
      } else {
        console.log('Setting barcodeError with generic message')
        setBarcodeError({
          message: 'Stain classifier data is not available',
        })
      }
    } else {
      setBarcodeError(null)
    }
  }, [barcodeData])

  const handleLogout = () => {
    dispatch(userLogout())
    localStorage.clear()
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    })
  }
  const handleSettingsClose = () => {
    setAnchorEl(null)
  }

  const handleProfile = (e) => {
    setAnchorEl(null)
    setProfileOpen(true)
  }

  const handleClose = (e) => {
    e.stopPropagation()
    setProfileOpen(false)
  }

  const handleReportOpen = useCallback(
    (val) => {
      setViewerOpen(false)
      setSummaryOpen(false)
      setReportOpen(true)
      setLocation(val.location)
    },
    [viewerOpen, summaryOpen, reportOpen, location],
  )

  const handleViewerOpen = useCallback(
    (val) => {
      setViewerOpen(true)
      setSummaryOpen(false)
      setReportOpen(false)
      setLocation(val.location)
      setSlide(val.slide)
      setStain(val.stain)
      setStainsDropdown(val.stainData)
      setShow(val.isShow)
      const stains = val.stainData

      // Find the current stain in the stain data
      const currentStain = stains.find((stain) => {
        const stainName = val.slide + ' ' + val.stain
        return stain.name.toUpperCase() === stainName.toUpperCase()
      })

      // Set diagnosis status based on the found stain
      if (currentStain) {
        setDiagnosed(currentStain.isDiagnosed === null ? false : currentStain.isDiagnosed)
        setDontReport(currentStain.isDontReport === null ? false : currentStain.isDontReport)
      } else {
        // Fallback: set to false if stain not found
        setDiagnosed(false)
        setDontReport(false)
      }
    },
    [
      isDiagnosed,
      isDontReport,
      viewerOpen,
      summaryOpen,
      reportOpen,
      slide,
      stain,
      location,
      stainsDropdown,
      barcodeData,
    ],
  )

  const handleClassifierSelectedValue = function (classifierSelectedValue) {
    setClassifier(classifierSelectedValue)
  }

  const handleSummaryOpen = useCallback(
    (val) => {
      setViewerOpen(false)
      setSummaryOpen(true)
      setReportOpen(false)
    },
    [viewerOpen, summaryOpen, reportOpen],
  )

  const handlePremerisDialogClose = useCallback(
    (val) => {
      setViewerOpen(false)
      setSummaryOpen(false)
      setReportOpen(false)
      handlePremerisClose()
    },
    [viewerOpen, summaryOpen, reportOpen],
  )

  const handleReleaseReport = async () => {
    setIsReadOnlyMode(true)
    await dispatch(getReportHistory({ trfBarcode: trfBarcode }))
    handleTRFComplete()
  }

  const handleConsultCase = useCallback(() => {
    setIsReadOnlyMode(true)
  }, [readOnlyMode])

  const handleConsultComplete = useCallback(() => {
    setIsReadOnlyMode(false)
  }, [readOnlyMode])

  const getAge = (dob) => {
    const today = new Date()
    const birthDate = new Date(dob)
    let age = today.getFullYear() - birthDate.getFullYear()
    const m = today.getMonth() - birthDate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const handleDob = (dob) => {
    return dob.slice(0, 10)
  }

  return (
    <Box>
      <Dialog
        fullScreen
        open={true}
        onClose={handlePremerisDialogClose}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        style={{ margin: '0px' }}
      >
        <DialogContent style={{ margin: '0px', height: '100%', padding: '0px', backgroundColor: '#f2f2f2' }}>
          <Box className={classes.summaryBox}>
            <AppBar
              position="static"
              sx={{
                color: '#0A688D',
                backgroundColor: 'rgba(255, 255, 255, 1)',
              }}
            >
              <Toolbar>
                <div style={{ display: 'flex', flexGrow: 1 }}>
                  <div color="inherit" style={{ flexGrow: 1, padding: '8px', marginTop: '10px' }}>
                    <Link to="/" className={classes.link}>
                      <Logo width={'81'} height={'41'} />
                    </Link>
                    <span className={classes.diagnosisTitle}>Case Diagnosis</span>
                  </div>
                  <Box
                    sx={{
                      '& .MuiGrid-item': {
                        paddingLeft: '15px !important',
                        paddingTop: '10px !important',
                      },
                      width: '60%',
                    }}
                  >
                    {summaryOpen === false && (
                      <Grid
                        container
                        rowSpacing={1}
                        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                        className={classes.patientInfoHeader}
                      >
                        <Grid item xs={1.5}>
                          <div>
                            <b>Case</b>
                          </div>
                          <div>{caseNumber}</div>
                        </Grid>
                        {trfData && trfData.patientInfo && (
                          <Grid item xs={2}>
                            <div>
                              <b>Patient</b>
                            </div>
                            <div>
                              {trfData.patientInfo.PatLastName}, {trfData.patientInfo.PatFirstName}
                            </div>
                          </Grid>
                        )}
                        {trfData && trfData.patientInfo && trfData.patientInfo.PatDOB && (
                          <Grid item xs={1.5}>
                            <div>
                              <b>DOB</b>
                            </div>
                            <div>
                              {trfData?.patientInfo?.PatDOB &&
                                trfData?.patientInfo?.PatAge !== 0 &&
                                handleDob(trfData?.patientInfo?.PatDOB)}
                            </div>
                          </Grid>
                        )}
                        <Grid item xs={1}>
                          <div>
                            <b>Age</b>
                          </div>
                          <div>
                            {trfData?.patientInfo?.PatAge && trfData?.patientInfo?.PatAge !== 0
                              ? trfData?.patientInfo?.PatAge
                              : ''}
                          </div>
                        </Grid>
                        <Grid item xs={2}>
                          <div>
                            <b>Practice State</b>
                          </div>
                          <div>{trfData?.practiceInfo?.PraSt}</div>
                        </Grid>
                        <Grid item xs={2}>
                          <div>
                            <b>Biopsy Date</b>
                          </div>
                          <div>
                            {trfData &&
                              trfData?.trfInfo?.TrfReceiptDate &&
                              handleDob(trfData?.trfInfo?.TrfProcedureDate)}
                          </div>
                        </Grid>
                        <Grid item xs={2}>
                          <div>
                            <b>Received</b>
                          </div>
                          <div>
                            {trfData && trfData?.trfInfo?.TrfReceiptDate && handleDob(trfData?.trfInfo?.TrfReceiptDate)}
                          </div>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                  <div style={{ flexGrow: 1 }}>
                    {' '}
                    {/* {role ? ( */}
                    {name ? (
                      <div style={{ display: 'flex', float: 'right', padding: '15px' }}>
                        <span className={classes.span}>
                          {` ${name}`}
                          <sub>({role})</sub>
                        </span>
                        <Tooltip title="Account settings">
                          <IconButton
                            aria-label="more"
                            aria-controls="long-menu"
                            aria-haspopup="true"
                            onClick={handleSettingsClick}
                            size="large"
                          >
                            <AccountCircle className={classes.avatar} />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          id="long-menu"
                          anchorEl={anchorEl}
                          keepMounted
                          open={handleSettingsOpen}
                          onClose={handleSettingsClose}
                          PaperProps={{
                            elevation: 0,
                            sx: {
                              overflow: 'visible',
                              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                              mt: 1.5,
                              '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                              },
                              '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                              },
                            },
                          }}
                          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                          <MenuItem onClick={handleProfile}>
                            <Avatar />
                            My Profile
                            {profileOpen && (
                              <UserProfileView
                                user={user}
                                role={role}
                                handleClose={handleClose}
                                profileOpen={profileOpen}
                                setProfileOpen={setProfileOpen}
                              />
                            )}
                          </MenuItem>
                          <MenuItem onClick={handleLogout}>
                            <Logout style={{ marginRight: '5px' }} />
                            Logout
                          </MenuItem>
                        </Menu>
                      </div>
                    ) : null}
                  </div>
                </div>
              </Toolbar>
            </AppBar>
            {(location ||
              slide ||
              stain ||
              summaryOpen ||
              reportOpen ||
              viewerOpen ||
              stainsDropdown ||
              trfBarcode ||
              isDiagnosed !== undefined ||
              isDontReport !== undefined) && (
              <DiagnosisNavbar
                caseView={caseView}
                url={urlLink}
                location={location}
                slide={slide}
                stain={stain}
                stainsDropdown={stainsDropdown}
                trfBarcode={trfBarcode}
                isSummary={summaryOpen}
                isViewer={viewerOpen}
                isReport={reportOpen}
                handleSummaryOpen={handleSummaryOpen}
                handleViewerOpen={handleViewerOpen}
                handleReportOpen={handleReportOpen}
                handlePremerisClose={handlePremerisDialogClose}
                handleClassifierSelectedValue={handleClassifierSelectedValue}
                barcodeError={barcodeError}
                setBarcodeError={setBarcodeError}
              />
            )}
            {summaryOpen && isAmendFlow !== undefined && (
              <Summary
                caseView={caseView}
                handleViewerOpen={handleViewerOpen}
                handleReportOpen={handleReportOpen}
                trfBarcode={trfBarcode}
                isAmendFlow={isAmendFlow}
                isReadOnlyMode={readOnlyMode || isReadOnlyMode}
                handleReleaseReport={handleReleaseReport}
                handleConsultCase={handleConsultCase}
                handleConsultComplete={handleConsultComplete}
              />
            )}
            {viewerOpen && slide && stain && isDiagnosed !== undefined && isDontReport !== undefined && (
              <Viewer
                handleReportOpen={handleReportOpen}
                stain={stain}
                trfBarcode={trfBarcode}
                slide={slide}
                location={location}
                isShow={isShow}
                setShow={setShow}
                isDiagnosed={isDiagnosed}
                isDontReport={isDontReport}
                isReadOnlyMode={readOnlyMode || isReadOnlyMode}
                handleViewerOpen={handleViewerOpen}
                classifier={classifier}
                barcodeError={barcodeError}
              />
            )}
            {reportOpen && location !== undefined && pseudoProcedureCode !== undefined && (
              <Report
                trfBarcode={trfBarcode}
                location={location}
                pseudoProcedureCode={pseudoProcedureCode}
                handleViewerOpen={handleViewerOpen}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
